import React, { useMemo } from 'react';
import { GitPullRequestDraft } from 'lucide-react';
import { EMPTY_STREAM_READ_SESSION, type StreamWorkspaceReadPanelProps } from './streamMappingWorkspaceTypes';
import { parseFlow } from '../utils/flowParser';

type StreamMappingStoryRailProps = StreamWorkspaceReadPanelProps & {
  full?: boolean;
};

const StreamMappingStoryRail: React.FC<StreamMappingStoryRailProps> = ({
  stream,
  session = EMPTY_STREAM_READ_SESSION,
  full = false,
}) => {
  const flowStatements = useMemo(
    () => parseFlow(session.newModel.flowText || '').statements,
    [session.newModel.flowText],
  );
  const spineSteps = useMemo(
    () => [...session.spine].sort((a, b) => a.order - b.order),
    [session.spine],
  );

  const hasSpine = spineSteps.length > 0;
  const hasFlow = flowStatements.length > 0;
  const visibleSpine = full ? spineSteps : spineSteps.slice(0, 8);

  return (
    <section
      className={`smw-story${full ? ' smw-story--full' : ''}`}
      aria-label={`Story spine for ${stream.name}`}
    >
      <div className="smw-panel-head">
        <div>
          <h2>Story spine</h2>
          <p>The plain-language thread stays visible while the map catches up.</p>
        </div>
        <GitPullRequestDraft size={18} aria-hidden="true" />
      </div>

      {session.sessionLoading ? (
        <p className="smw-muted">Opening the captured story...</p>
      ) : hasSpine ? (
        <div className="smw-spine" aria-live="polite">
          <div className="smw-spine-summary">
            <span>{spineSteps.length} step{spineSteps.length === 1 ? '' : 's'}</span>
            {session.callStatus === 'On Call' ? <span>Listening</span> : null}
          </div>
          <ol className="smw-story-list smw-story-list--spine">
            {visibleSpine.map((step) => {
              const needsLook = step.state === 'uncertain' || step.needsCheck;
              return (
                <li
                  key={`${step.order}-${step.label}`}
                  className={needsLook ? 'smw-story-row smw-story-row--check' : 'smw-story-row'}
                >
                  <span>{step.order}</span>
                  <div>
                    <strong>{step.label}</strong>
                    {needsLook ? <small>Needs a look</small> : null}
                  </div>
                </li>
              );
            })}
          </ol>
          {spineSteps.length > visibleSpine.length ? (
            <p className="smw-story-more">
              {spineSteps.length - visibleSpine.length} more step
              {spineSteps.length - visibleSpine.length === 1 ? '' : 's'} in the capture.
            </p>
          ) : null}
        </div>
      ) : hasFlow ? (
        <ol className="smw-story-list">
          {(full ? flowStatements : flowStatements.slice(0, 6)).map((statement) => (
            <li key={statement.id} className="smw-story-row">
              <span>{statement.id}</span>
              <div>
                <strong>{statement.name || 'Captured step'}</strong>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="smw-muted">
          The spine will build here as the stream is narrated through chat.
        </p>
      )}
    </section>
  );
};

export default StreamMappingStoryRail;
